/* eslint-disable navina/restricted-syntax -- We need our own error classes to extend Error. */
enum NavinaErrorKind {
	NavinaCookiesError = 'NavinaCookiesError',
	NavinaNonSpecificError = 'NavinaNonSpecificError',
	MissingPayloadParameterError = 'MissingPayloadParameterError',
	AuthenticationError = 'AuthenticationError',
	InvalidUserInputError = 'InvalidUserInputError',
	EntityNotFoundError = 'EntityNotFoundError',
}

const ERRORS = {
	[NavinaErrorKind.NavinaCookiesError]: {
		code: 'NAVINA_COOKIES_NOT_ENABLED',
		message: 'Cookies are not enabled',
	},
	[NavinaErrorKind.NavinaNonSpecificError]: {
		code: 'NAVINA_NON_SPECIFIC_ERROR',
		message: 'An error occurred (non-specific)',
	},
	[NavinaErrorKind.MissingPayloadParameterError]: {
		code: 'MISSING_PAYLOAD_PARAMETER',
		message: 'A required parameter is missing from the payload.',
	},
	[NavinaErrorKind.AuthenticationError]: {
		code: 'AUTHENTICATION_ERROR',
		message: "We couldn't verify your login",
	},
	[NavinaErrorKind.InvalidUserInputError]: {
		code: 'INVALID_USER_INPUT',
		message: 'The given input is invalid.',
	},
	[NavinaErrorKind.EntityNotFoundError]: {
		code: 'ENTITY_NOT_FOUND',
		message: 'Entity not found.',
	},
} as const satisfies Record<
	NavinaErrorKind,
	{
		readonly code: string;
		readonly message: string;
	}
>;

type ErrorCode = (typeof ERRORS)[keyof typeof ERRORS]['code'];

interface NavinaErrorProps {
	readonly title: string;
	readonly description: string;
	readonly code: NavinaErrorKind;
}
export class NavinaError extends Error {
	public title: string;
	public description: string;
	public code: ErrorCode;

	constructor({ title, description, code: outerCode }: NavinaErrorProps) {
		const error = ERRORS[outerCode];
		const { message, code } = error;

		super(message);
		this.title = title;
		this.description = description;
		this.message = `${message}: (${description})`;
		this.code = code;
	}
}

export class NavinaCookiesError extends NavinaError {
	constructor() {
		super({
			title: 'Cookies Are Disabled',
			description:
				'Your browser is blocking cookies, which prevents Navina from loading properly. Please ask your IT department to adjust your browser settings.',
			code: NavinaErrorKind.NavinaCookiesError,
		});
	}
}

export class NavinaThirdPartyCookiesError extends NavinaError {
	constructor() {
		super({
			title: 'Third-Party Cookies Blocked',
			description:
				'Your browser is blocking third-party cookies, which prevents Navina from loading properly. Please ask your IT department to adjust your browser settings.',
			code: NavinaErrorKind.NavinaCookiesError,
		});
	}
}

export class MissingPayloadParameterError extends NavinaError {
	constructor(description: string) {
		super({
			title: 'Missing Payload Parameter',
			description,
			code: NavinaErrorKind.MissingPayloadParameterError,
		});
	}
}

export class InitializationError extends NavinaError {
	constructor(description: string) {
		super({
			title: 'Initialization Error',
			description,
			code: NavinaErrorKind.NavinaNonSpecificError,
		});
	}
}

export class AuthenticationError extends NavinaError {
	readonly isHiddenError: boolean;
	constructor({
		description,
		isHiddenError = false,
	}: {
		readonly description: string;
		readonly isHiddenError?: boolean;
	}) {
		super({
			title: 'Authentication Error',
			description,
			code: NavinaErrorKind.AuthenticationError,
		});

		this.isHiddenError = isHiddenError;
	}
}

export class InvalidUserInputError extends NavinaError {
	constructor(description: string) {
		super({
			title: 'Invalid User Input',
			description,
			code: NavinaErrorKind.InvalidUserInputError,
		});
	}
}

export class EntityNotFoundError extends NavinaError {
	constructor(description: string) {
		super({
			title: 'Entity Not Found',
			description,
			code: NavinaErrorKind.EntityNotFoundError,
		});
	}
}

/* eslint-enable navina/restricted-syntax */
